import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/api';
import { useAuthStore } from '../store/auth.store';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { CGU_SECTIONS, PRIVACY_SECTIONS, type CguSection } from '../content/cgu-content';
import { Loader2, ScrollText, CheckSquare, Square } from 'lucide-react';

function parseSections(text: string): CguSection[] {
  if (!text) return [];
  const blocks = text.split('\n\n');
  const sections: CguSection[] = [];
  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed) continue;
    const firstNewline = trimmed.indexOf('\n');
    if (firstNewline === -1) {
      sections.push({ title: trimmed, content: '' });
    } else {
      sections.push({
        title: trimmed.substring(0, firstNewline).trim(),
        content: trimmed.substring(firstNewline + 1).trim(),
      });
    }
  }
  return sections;
}

export default function AcceptCguPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();
  const { isDesktop } = useBreakpoint();

  const [accepted, setAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [cguSections, setCguSections] = useState<CguSection[]>(CGU_SECTIONS);
  const [privacySections, setPrivacySections] = useState<CguSection[]>(PRIVACY_SECTIONS);

  useEffect(() => {
    authApi.getLegalTexts().then((data) => {
      if (data.cguText) setCguSections(parseSections(data.cguText));
      if (data.privacyText) setPrivacySections(parseSections(data.privacyText));
    }).catch(() => {
      // Keep hardcoded defaults
    });
  }, []);

  const handleAccept = async () => {
    if (!accepted) return;
    setIsLoading(true);
    setError('');

    try {
      await authApi.acceptCgu();
      if (user) {
        setUser({ ...user, cguAccepted: true });
      }
      // Redirect to 2FA setup if needed, otherwise dashboard
      if (user?.mustSetup2FA) {
        navigate('/setup-2fa', { replace: true });
      } else {
        navigate(isDesktop ? '/dashboard' : '/map', { replace: true });
      }
    } catch {
      setError(t('cgu.accept_error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden">
            <img src="/logo.png" alt="TrackFleet" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">TrackFleet</h1>
            <p className="text-xs text-gray-500">{t('cgu.header_subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
              <ScrollText className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('cgu.page_title')}
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              {t('cgu.page_subtitle')}
            </p>
          </div>

          {/* CGU Sections */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{t('cgu.section_cgu')}</h3>
            </div>
            <div className="px-6 py-4 max-h-[50vh] overflow-y-auto space-y-6">
              {cguSections.map((section, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Privacy Sections */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-8">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">{t('cgu.section_privacy')}</h3>
            </div>
            <div className="px-6 py-4 max-h-[40vh] overflow-y-auto space-y-6">
              {privacySections.map((section, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-gray-900 mb-2">{section.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm mb-4">
              {error}
            </div>
          )}

          {/* Checkbox + Button */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <label
              className="flex items-start gap-3 cursor-pointer mb-6"
              onClick={() => setAccepted(!accepted)}
            >
              {accepted ? (
                <CheckSquare className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              ) : (
                <Square className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
              )}
              <span className="text-sm text-gray-700">
                {t('cgu.checkbox_label')}
              </span>
            </label>

            <button
              onClick={handleAccept}
              disabled={!accepted || isLoading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t('cgu.accept_button')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
